import { useParams } from 'react-router'
import { FormWrapper } from '@/form-control'
import { S3FilenameField, TextAreaField, TextField } from '@/form-control/fields'
import TagField from '@/form-control/fields/TagField'
import ROUTES from '@/ROUTES'

import { IPublicResource } from '@/MODELS/publicResource.model'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'

export default function AdminResourceForm(props: { id?: string }) {
    const params = useParams()
    const id = props?.id ?? params?.id ?? 'new'

    return (
        <>
            <AdminPageHeader heading={id ? 'Edit Resource' : 'Create New Resource'} />
            <FormWrapper<IPublicResource>
                endpoint={ROUTES.admin.publicResource}
                id={id}
                redirectAfterSubmit='/admin/resources'
                defaultValues={{ resourceType: 'link', displayBehavior: 'new_tab' }}
                additionalSubmissionRowContent={
                    <div className='flex items-center justify-between w-full gap-x-2 h-fit'>
                        <Button.Back />
                    </div>
                }
            >
                {(f, { formValues, setFormValues }) => {
                    const resourceType = formValues.resourceType || 'link'

                    return (
                        <div className='bg-white rounded-xl shadow-sm border-gray-200 overflow-hidden'>
                            <div className='p-6 space-y-6'>
                                <div className='grid lg:grid-cols-2 gap-6'>
                                    <TextField {...f('title')} label='Title' />
                                    <div className='lg:col-span-2'>
                                        <TextAreaField {...f('description')} label='Description' />
                                    </div>
                                </div>
                            </div>

                            <div className='p-6 space-y-6'>
                                {/* Resource Type Toggle */}
                                <div>
                                    <label className='field-label'>Resource Type</label>
                                    <div className='flex rounded-lg border border-gray-200 overflow-hidden w-fit'>
                                        <button
                                            type='button'
                                            onClick={() => setFormValues((prev) => ({ ...prev, resourceType: 'link' }))}
                                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                                                resourceType === 'link'
                                                    ? 'bg-brand-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            External Link
                                        </button>
                                        <button
                                            type='button'
                                            onClick={() => setFormValues((prev) => ({ ...prev, resourceType: 'file' }))}
                                            className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-200 ${
                                                resourceType === 'file'
                                                    ? 'bg-brand-600 text-white'
                                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            Uploaded File
                                        </button>
                                    </div>
                                </div>

                                <div className='grid lg:grid-cols-2 gap-6'>
                                    <TextField {...f('category')} label='Category' />
                                    <div className='field-container'>
                                        <label className='field-label'>Display Behavior</label>
                                        <select
                                            value={formValues.displayBehavior || 'new_tab'}
                                            onChange={(e) =>
                                                setFormValues((prev) => ({ ...prev, displayBehavior: e.target.value as IPublicResource['displayBehavior'] }))
                                            }
                                            className='field-input'
                                        >
                                            {resourceType === 'file' && (
                                                <option value='modal'>Open in Modal</option>
                                            )}
                                            <option value='new_tab'>Open in New Tab</option>
                                            <option value='new_window'>Open in New Window</option>
                                        </select>
                                        {resourceType === 'link' && (
                                            <p className='mt-1 text-xs text-gray-400 max-w-full break-words'>
                                                Note: 'Open in Modal' is only available for uploaded files, since most external sites block iframe embedding.
                                            </p>
                                        )}
                                    </div>

                                    {resourceType === 'link' && (
                                        <TextField {...f('link')} label='External Link' />
                                    )}

                                    {resourceType === 'file' && (
                                        <S3FilenameField
                                            {...f('s3ObjectId')}
                                            label='Upload File'
                                            showPreview={true}
                                            permissions={{ isViewableByAnyUser: true, isPublic: false }}
                                        />
                                    )}

                                    <TagField
                                        {...f('tags')}
                                        containerClass='col-span-2'
                                        placeholder='Type and press Enter, Tab, or comma to add tags'
                                    />
                                </div>
                            </div>
                        </div>
                    )
                }}
            </FormWrapper>
        </>
    )
}
